import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type { Macro } from '@rapoo/device-sdk'

import { useDeviceStore } from '@/stores'
import {
  LOOP_MODE_HOLD,
  LOOP_MODE_TOGGLE,
  type MacroListItem,
  type MacroPlayMode,
} from '../../../keyboard/keys/components/TabMacro/types'
import { defaultReportError } from '@/composables/useErrorLogger'

/** 鼠标宏数量上限（VTNrf54L 仅 12 条） */
const MAX_MOUSE_MACRO_COUNT = 12

const createEmptyMacro = ({
  index,
  name,
  mode = 1,
}: {
  index: number
  name: string
  mode?: MacroPlayMode
}): Macro => ({
  name,
  index,
  loop: mode === 1 ? 1 : mode === 2 ? LOOP_MODE_TOGGLE : LOOP_MODE_HOLD,
  package: [],
  ignoreDelay: false,
})

/* ---- 模块级单例状态，所有调用方共享 ---- */
const loading = ref(false)
const macroList = ref<MacroListItem[]>([])

/**
 * 鼠标宏仓储（mouseKeys feature）。
 *
 * 与 keyboard 版接口保持一致，便于在 TabMacro 组件中通过
 * provide/inject 替换实现而不修改组件内部逻辑。
 */
export function useMouseMacroRepository() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const mouseKeys = computed(() => currentDevice.value?.features.mouseKeys)

  // 防重入：每次调用递增 loadId，stream 回调中检查是否过期
  let loadId = 0

  const loadMacroList = async (): Promise<void> => {
    const feature = mouseKeys.value
    if (!feature) {
      macroList.value = []
      return
    }

    const currentLoadId = ++loadId
    loading.value = true
    macroList.value = []

    const seenIndices = new Set<number>()

    try {
      for await (const macros of feature.getMacroListStream()) {
        // 如果期间触发了新的 loadMacroList，放弃本次流
        if (currentLoadId !== loadId) return

        for (const item of macros) {
          if (item.index == null) continue
          if (item.index < 0 || item.index >= MAX_MOUSE_MACRO_COUNT) continue
          // 按 index 去重，防止协议层重复推送
          if (seenIndices.has(item.index)) continue
          seenIndices.add(item.index)

          macroList.value.push({
            name: item.name,
            index: item.index as number,
          })
        }
      }
    } catch (error) {
      if (currentLoadId !== loadId) return
      console.error('[MouseTabMacro] loadMacroList failed:', error)
      defaultReportError(`[MouseTabMacro] loadMacroList failed: ${JSON.stringify(error)}`)
      macroList.value = []
    } finally {
      if (currentLoadId === loadId) {
        loading.value = false
      }
    }
  }

  const readMacroByIndex = async (index: number): Promise<Macro> => {
    const feature = mouseKeys.value
    if (!feature) {
      return createEmptyMacro({ index, name: `Macro${index + 1}` })
    }

    try {
      const macro = await feature.getMacroByIndex(index)
      return {
        ...createEmptyMacro({ index, name: macro.name || `Macro${index + 1}` }),
        ...macro,
        index,
      }
    } catch (error) {
      console.error('[MouseTabMacro] readMacroByIndex failed:', error)
      defaultReportError(`[MouseTabMacro] readMacroByIndex failed: ${JSON.stringify(error)}`)
      return createEmptyMacro({ index, name: `Macro${index + 1}` })
    }
  }

  const saveMacroByIndex = async (macro: Macro, index: number) => {
    const normalized: Macro = {
      ...createEmptyMacro({ index, name: macro.name || `Macro${index + 1}` }),
      ...macro,
      index,
    }

    const feature = mouseKeys.value
    if (!feature) return

    try {
      await feature.setMacroByIndex(normalized, index)
    } catch (error) {
      console.error('[MouseTabMacro] saveMacroByIndex failed:', error)
      defaultReportError(`[MouseTabMacro] saveMacroByIndex failed: ${JSON.stringify(error)}`)
    }
  }

  const createMacro = async (payload: MacroListItem): Promise<Macro> => {
    const macro = createEmptyMacro({ index: payload.index, name: payload.name })
    await saveMacroByIndex(macro, payload.index)
    return macro
  }

  const renameMacro = async ({ index, name }: MacroListItem): Promise<Macro> => {
    const current = await readMacroByIndex(index)
    const renamed = {
      ...current,
      index,
      name,
    }
    await saveMacroByIndex(renamed, index)
    return renamed
  }

  const deleteMacro = async (index: number) => {
    const feature = mouseKeys.value
    if (!feature) return

    const empty = {
      ...createEmptyMacro({ index, name: '' }),
      package: [],
    } as Macro

    try {
      await feature.setMacroByIndex(empty, index)
    } catch (error) {
      console.error('[MouseTabMacro] deleteMacro failed:', error)
      defaultReportError(`[MouseTabMacro] deleteMacro failed: ${JSON.stringify(error)}`)
    }
  }

  return {
    loading,
    macroList,
    loadMacroList,
    readMacroByIndex,
    saveMacroByIndex,
    createMacro,
    renameMacro,
    deleteMacro,
  }
}
